import * as React from "react";
import { useUser } from "../../../utils/supabase/useUser";
import { Option } from "fp-ts/Option";
import { option } from "fp-ts";
import { EventDetail, useEventDetail } from "../../../hooks/event";
import { Query, useRouteQuery } from "../../../utils/routing/routing";
import { eventAttendRoute } from "../../../routes/xathlo";
import { constNull, pipe } from "fp-ts/function";
import { NextRouter, useRouter } from "next/router";
import { useAlertOnQueryError } from "../../../hooks/useAlertOnQueryError";
import { queryStateData } from "../../../utils/supabase/QueryState";
import { CenteredProgress } from "../../../components/common/CenteredProgress";
import { Alert, Box, Container, Grid, Typography } from "@mui/material";
import { LocalDate } from "../../../components/common/date/LocalDate";
import { Sign } from "../../../components/xathlo/sign/Sign";
import MyRegistrations from "../../../components/xathlo/MyRegistrations";
import { GlobalAlert } from "../../../components/common/GlobalAlerts";
import { redirectToCodec } from "../../../routes/user";
import Image from "next/legacy/image";
import { NextSeo } from "next-seo";
import { useFirstRenderDate } from "../../../utils/useFirstRenderDate";
import { isRegistrationOpen, RaceId } from "../../../domain/event";
import { yearFromLocalDate } from "../../../domain/common";
import { StartListBtn } from "../../../components/org/event/StartListBtn";

export default () => {
    const query = useRouteQuery(eventAttendRoute);
    const router = useRouter();
    React.useEffect(() => {
        if (option.isNone(query) && router.isReady) {
            router.push("/404"); // TODO
        }
    }, [query, router.isReady]);

    const eventDetailQuery = useEventDetailQuery(query);
    useAlertOnQueryError(eventDetailQuery.state);

    const preferredRaceId = pipe(
        query,
        option.chain((q) => option.fromNullable(q.raceId))
    );

    return pipe(
        eventDetailQuery.state,
        queryStateData,
        option.fold(
            () => (
                <>
                    <NextSeo noindex={true} />
                    <Box
                        sx={{
                            width: "100vw",
                            height: "100vh",
                        }}
                    >
                        <CenteredProgress fullHeight />
                    </Box>
                </>
            ),
            (event) =>
                pipe(
                    event.banner_image,
                    option.fromNullable,
                    option.fold(
                        () => (
                            <Container maxWidth={"md"}>
                                <Attend
                                    router={router}
                                    event={event}
                                    preferredRaceId={preferredRaceId}
                                />
                            </Container>
                        ),
                        (bannerImage) => {
                            const height = "100vh";
                            return (
                                <Grid
                                    direction={"row-reverse"}
                                    container
                                    component="main"
                                    sx={{
                                        minHeight: height,
                                        maxHeight: height,
                                    }}
                                >
                                    <Grid
                                        item
                                        md={6}
                                        xs={12}
                                        sx={{
                                            overflowY: "scroll",
                                            maxHeight: height,
                                        }}
                                    >
                                        <Attend
                                            router={router}
                                            event={event}
                                            preferredRaceId={preferredRaceId}
                                        />
                                    </Grid>
                                    <Grid
                                        item
                                        md={6}
                                        xs={12}
                                        sx={{
                                            position: "relative",
                                            display: {
                                                xs: "none",
                                                sm: "block",
                                            },
                                        }}
                                    >
                                        <Image
                                            src={bannerImage.url}
                                            layout={"fill"}
                                            objectPosition={"50% 50%"}
                                            objectFit={"cover"}
                                            alt={event.title}
                                        />
                                    </Grid>
                                </Grid>
                            );
                        }
                    ),
                    (content) => (
                        <>
                            <NextSeo noindex={true} title={event.title} />
                            {content}
                        </>
                    )
                )
        )
    );
};

interface AttendProps {
    event: EventDetail;
    router: NextRouter;
    preferredRaceId: Option<RaceId>;
}
const Attend = (p: AttendProps) => {
    const userO = useUser();
    const [alert, setAlert] = React.useState<Option<GlobalAlert>>(option.none);
    const firstRenderDate = useFirstRenderDate();

    return (
        <Box
            sx={{
                p: {
                    xs: 1,
                    sm: 2,
                    md: 4,
                },
            }}
        >
            <Typography variant={"h4"} component={"h2"} gutterBottom>
                Registrace:
            </Typography>
            <Typography variant={"h3"} component={"h1"}>
                {p.event.title}
            </Typography>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                {p.event.date == null ? null : (
                    <Typography
                        variant={"h5"}
                        component={"div"}
                        sx={{
                            marginBottom: (theme) => theme.spacing(2),
                        }}
                    >
                        <LocalDate date={p.event.date} />
                    </Typography>
                )}
                <StartListBtn eventId={p.event.id} races={p.event.races} />
            </Box>

            {pipe(
                alert,
                option.fold(constNull, (a) => (
                    <Alert
                        sx={{
                            "& .MuiAlert-message": {
                                overflowWrap: "anywhere",
                            },
                            maxWidth: "100%",
                            marginBottom: (theme) => theme.spacing(2),
                        }}
                        onClose={() => {
                            setAlert(option.none);
                        }}
                        severity={a.severity}
                    >
                        {a.message}
                    </Alert>
                ))
            )}

            {pipe(
                isRegistrationOpen(firstRenderDate, p.event),
                option.fold(
                    () => (
                        <Typography>
                            Online registrace je uzavřena. Můžete se registrovat
                            na místě v den závodu.
                        </Typography>
                    ),
                    (registrationOpen) => {
                        return pipe(
                            userO,
                            option.fold(
                                () => (
                                    <Sign
                                        router={p.router}
                                        setAlert={setAlert}
                                        redirectTo={pipe(
                                            p.router.asPath,
                                            redirectToCodec.decode,
                                            option.fromEither
                                        )}
                                    />
                                ),
                                (user) => (
                                    <MyRegistrations
                                        eventId={p.event.id}
                                        rules={p.event.rules}
                                        paymentMethod={p.event.paymentMethod}
                                        organizationId={p.event.organization_id}
                                        user={user}
                                        eventYear={yearFromLocalDate(
                                            registrationOpen.date // TODO p.event.date
                                        )}
                                        eventRaceIdList={p.event.races.map(
                                            (race) => race.id
                                        )}
                                        preferredRaceId={p.preferredRaceId}
                                    />
                                )
                            )
                        );
                    }
                )
            )}
        </Box>
    );
};

const useEventDetailQuery = (query: Option<Query<typeof eventAttendRoute>>) =>
    pipe(
        query,
        option.map((q) => q.eventId),
        useEventDetail
    );
